import { useEffect, useMemo, useRef } from "react";

import { useGetDispatchCenters } from "@/api/dispatch-centers";
import { filterAndSortCentersForCarriers } from "@/components/dispatch-center-filter";
import { useCarrierScope } from "@/contexts/carrier-scope-context";
import { operationalDateKey } from "@/lib/operational-date";
import { useDispatchDateStore } from "@/features/dashboard/dispatch-date.store";

export function useDispatchOperationalTimeZone(): string | null {
  const { carrierIds, dispatchCenterIds } = useCarrierScope();
  const { data: allCenters } = useGetDispatchCenters();
  return useMemo(() => {
    const centers = filterAndSortCentersForCarriers(
      allCenters ?? [],
      carrierIds
    ).filter(
      (center) =>
        dispatchCenterIds.length === 0 || dispatchCenterIds.includes(center.id)
    );
    const timeZones = new Set(centers.map((center) => center.timeZone));
    return timeZones.size === 1 ? ([...timeZones][0] ?? null) : null;
  }, [allCenters, carrierIds, dispatchCenterIds]);
}

export function useInitializeDispatchDate(timeZone: string | null): void {
  const setSelectedDate = useDispatchDateStore(
    (state) => state.setSelectedDate
  );
  const initializedTimeZone = useRef<string | null>(null);

  useEffect(() => {
    if (!timeZone || initializedTimeZone.current === timeZone) return;
    const dateKey = operationalDateKey(new Date(), timeZone);
    if (!dateKey) return;
    initializedTimeZone.current = timeZone;
    setSelectedDate(dateKey);
  }, [setSelectedDate, timeZone]);
}
